// includes/js-src/janus-engine/pl/calc-pl.ts — calc_pl() twin: the orchestrator (calc_pl_system.php:61–308 minus the
// server-only memo). PL = round(total_gp / 100, 2) over the 12 calculators in PHP call order; creatures floor at 0.
// Returns { pl, total_gp, breakdown, sep } or a refusal / error — never a partial number. Plan §6.2, §6.5.
import { plCostContract, type PlCostContract } from './contract';
import type { Row } from './read-context';
import { isObj, type PlReadContext } from './read-context';
import { PlRefusal, plPricingStack } from './xref';
import { newAccumulator, makeEnv, itemType, itemField, calculateEssencesAndSenses, calculateSpeeds, calculateSpecialChecks, calculateStatusEffectModifiers, calculateSkillTiers, calculateSkillRollModifiers, calculateRollTypeDamageApForceLightMisc, calculateArmorModifiers, calculateSpellsAndVitality, calculateInventoryCosts, calculateStatusApplicationAndTriggers, type Breakdown } from './passes';
import { calculateAbilitiesSteps } from './abilities';
import { phpRound } from './php-round';

export interface PlResult { pl: number; total_gp: number; breakdown: Breakdown; sep: Record<string, number> | null; passes: Array<{ name: string; total_gp: number }> }
export type CalcPlOutcome = { ok: true; result: PlResult } | { ok: false; unsupported: string } | { ok: false; error: { stage: string; message: string } };

/** array_filter($breakdown): drop empty buckets (falsy values). */
function pruneBreakdown(breakdown: Breakdown): Breakdown {
    const out: Breakdown = {};
    for (const [k, v] of Object.entries(breakdown)) {
        if (Array.isArray(v) ? v.length > 0 : isObj(v) && Object.keys(v).length > 0) out[k] = v;
    }
    return out;
}

export const PL_PASS_NAMES = ['essences_and_senses', 'speeds', 'special_checks', 'status_effect_modifiers', 'skill_tiers', 'skill_roll_modifiers', 'roll_type_damage_ap_force_light_misc', 'abilities', 'armor_modifiers', 'spells_and_vitality', 'inventory_costs', 'status_application_and_triggers'] as const;

/** calcPl(row, ctx): pure — reads the row and ctx.definition only; the same inputs give byte-identical output. */
export function calcPl(row: Row, ctx: PlReadContext, contract: PlCostContract = plCostContract): CalcPlOutcome {
    const steps = calcPlSteps(row, ctx, contract);
    let step = steps.next();
    while (!step.done) step = steps.next();
    return step.value;
}

/** Background heal uses the same passes and accumulator, yielding between passes and abilities. */
export async function calcPlBackground(row: Row, ctx: PlReadContext, checkpoint: () => Promise<void>): Promise<CalcPlOutcome> {
    const steps = calcPlSteps(row, ctx, plCostContract);
    try {
        let step = steps.next();
        while (!step.done) { await checkpoint(); step = steps.next(); }
        return step.value;
    } finally { steps.return({ ok: false, unsupported: 'pl-budget' }); }
}

function* calcPlSteps(row: Row, ctx: PlReadContext, contract: PlCostContract): Generator<void, CalcPlOutcome> {
    const passes: Array<{ name: string; total_gp: number }> = [];
    let stage = 'init';
    // a creature root is on the pricing stack while it prices: an ability that calls more of it skips that call (summon cycle)
    const stack = plPricingStack(ctx);
    let pushed = false;
    try {
        const env = makeEnv(ctx, contract);
        const acc = newAccumulator();
        const isCreature = ['creature', 'player_character'].includes(itemType(env, row));
        const rootItemId = isCreature ? String(itemField(env, row, 'item_id') ?? '').toUpperCase() : '';
        if (rootItemId !== '') { stack.push(rootItemId); pushed = true; }
        const run = (name: typeof PL_PASS_NAMES[number], fn: () => void): void => { stage = name; fn(); passes.push({ name, total_gp: acc.totalGp }); };
        run('essences_and_senses', () => calculateEssencesAndSenses(env, row, acc, isCreature));
        yield;
        run('speeds', () => calculateSpeeds(env, row, acc, isCreature));
        yield;
        run('special_checks', () => calculateSpecialChecks(env, row, acc));
        yield;
        run('status_effect_modifiers', () => calculateStatusEffectModifiers(env, row, acc, false, isCreature));
        yield;
        run('skill_tiers', () => calculateSkillTiers(env, row, acc));
        yield;
        run('skill_roll_modifiers', () => calculateSkillRollModifiers(env, row, acc));
        yield;
        run('roll_type_damage_ap_force_light_misc', () => calculateRollTypeDamageApForceLightMisc(env, row, acc, isCreature));
        yield;
        stage = 'abilities';
        yield* calculateAbilitiesSteps(env, row, acc);
        passes.push({ name: 'abilities', total_gp: acc.totalGp });
        yield;
        run('armor_modifiers', () => calculateArmorModifiers(env, row, acc));
        yield;
        run('spells_and_vitality', () => calculateSpellsAndVitality(env, row, acc));
        yield;
        run('inventory_costs', () => calculateInventoryCosts(env, row, acc));
        yield;
        run('status_application_and_triggers', () => calculateStatusApplicationAndTriggers(env, row, acc));
        stage = 'total';
        let totalGp = acc.totalGp;
        if (isCreature) totalGp = Math.max(0, totalGp);
        const pl = phpRound(totalGp / 100, 2);
        if (!Number.isFinite(totalGp) || !Number.isFinite(pl)) throw new Error('non-finite total_gp');
        return { ok: true, result: { pl, total_gp: totalGp, breakdown: pruneBreakdown(acc.breakdown), sep: acc.sep, passes } };
    } catch (e) {
        if (e instanceof PlRefusal) return { ok: false, unsupported: e.token };
        return { ok: false, error: { stage, message: e instanceof Error ? e.message : String(e) } };
    } finally {
        if (pushed) stack.pop();
    }
}
